import React, { useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCProgress, RCCTooltip } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import SortableHandle from "src/components/Sortable/Handle";
import { v4 } from "uuid";
import Arrow from "src/components/Arrow";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import WrapText from "src/components/WrapText";
import BadgeAssociate from "../Badge/badgeAssociate";
import { IMetba002 } from "../Perspective/interface";
import { IMetba003, IMetba005 } from "./interface";
import { ObjectiveCard } from "./styles";
import { useMap } from "../MapsContextProvider";

export interface IObjectiveCard{
    perspective: IMetba002;
    objective: IMetba003;
}

const Objective: React.FC<IObjectiveCard> = ({
	objective,
	perspective
}) => {
	const { translate } = useTranslation();
	const [labelLayout, setLabelLayout] = useState<"letter" | "word">("letter");

	const { administrator, superUser } = useAuth();
	const { checkIfHasSpecificPermission } = useAuth();	

	const { mapId, permissions, showCauseEffect, inEditCauseEffect,
		onMouseOverObjective, onMouseOutObjective, onCallbackDeleteCauseEffect,
		openModalObjective, openModalListIniciatives, onCallbackRelateEffect, onCallbackDeleteObjective 
	} = useMap();
	
	const timer = useRef<number>(0);
	const timerOut = useRef<number>(0);

	const mouseOverProgress = () => {
		timer.current = window.setTimeout(() => {
			onMouseOverObjective(objective);
		}, 500);
	};

	const mouseOutProgress = () => {
		timerOut.current = window.setTimeout(() => {
			onMouseOutObjective();
		}, 500);
		window.clearTimeout(timer.current);
	};

	const createArrow = () => (
		<>
			<div className="connector-arrow" id={objective.id}></div>
			{
				objective.metba005?.map((item: IMetba005) => (
					<Arrow 
						key={v4()}
						idMap={mapId}
						metba005={item}
						showDelete={checkIfHasSpecificPermission("maps/panel", PermissionEnum.CREATE_UPDATE) && (permissions >= 2 || administrator || superUser)}
						onCallbackDeleteCauseEffect={onCallbackDeleteCauseEffect}
					/>
				))
			}
		</>
	);

	return(
		<>
			<ObjectiveCard 
				key={objective.mea003Id} 
				color={perspective.mea002Cor}
				emphasis={objective.emphasis}
				inEditCauseEffect={inEditCauseEffect}
				onMouseOver={() => {setLabelLayout("word");}}
				onMouseOut={() => {setLabelLayout("letter");}}
			>
				{
					showCauseEffect && createArrow()
				}
				<div className="card-options-objectives">
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{( permissions >= 2 || administrator || superUser) && (
							<>
								<SortableHandle>
									<RCCTooltip 
										text={translate("Mover Objetivo")}
									>
										<RCCButton
											className="btn-option"
											icon={{
												name: "arrows", 
												color: "#FFF", 
												size: 12
											}}
											layout="isRounded"
											size="xs"
										/>
									</RCCTooltip>	
								</SortableHandle>
							</>
						)}											
					</CheckPermission>

					<RCCTooltip
						text={translate("Listar Iniciativas")}
					>
						<RCCButton
							className="btn-option"
							icon={{
								name: "list", 
								color: "#FFF", 
								size: 12
							}}
							layout="isRounded"
							size="xs"
							onClick={(e) => {
								e.stopPropagation();
								if(!openModalListIniciatives) 
									return; openModalListIniciatives({idObjective: objective.mea003Id}, objective.mea003Nome, undefined, permissions);}
							}
						/>
					</RCCTooltip>
					
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{( permissions >= 2 || administrator || superUser) && (
							<>
								{showCauseEffect && (
									<RCCTooltip
										text={objective.emphasis ? translate("Cancelar") : translate("Relacionar Causa/Efeito")}
									>
										<RCCButton
											icon={{
												name: "link", 
												color: "#FFF", 
												size: 12
											}}
											layout="isRounded"
											size="xs"
											onClick={(e) => {
												e.stopPropagation();
												onCallbackRelateEffect(objective);
											}}
										/>
									</RCCTooltip>
								)}
							</>
						)}											
					</CheckPermission>

					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						{( permissions >= 2 || administrator || superUser) && (
							<>
								<RCCTooltip
									text={translate("Editar Objetivo")}
								>
									<RCCButton
										className="btn-option"
										icon={{
											name: "pencil", 
											color: "#FFF", 
											size: 12
										}}
										layout="isRounded"
										size="xs"
										onClick={() => {if(!openModalObjective) return; openModalObjective(perspective, objective);}}
									/>
								</RCCTooltip>
							</>
						)}											
					</CheckPermission>

					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						{( permissions >= 2 || administrator || superUser) && (
							<>
								<RCCTooltip
									text={translate("Excluir Objetivo")}
								>
									<RCCButton
										className="btn-option"
										icon={{
											name: "trash", 
											color: "#FFF", 
											size: 12
										}}
										layout="isRounded"
										size="xs"
										onClick={() => onCallbackDeleteObjective(objective.mea003Id, objective.mea003Nome, perspective.mea002Nome)}
									/>
								</RCCTooltip>
							</>
						)}											
					</CheckPermission>

				</div>
				<div className="container-card">
					<RCCGrid 
						container
						className="card-header"
						justifyContent="space-between"
					>
						<h5>
							<RCCGrid styleInline="display: flex">
								{
									objective.factorCriticalSuccess && 
									<RCCTooltip 
										text={translate("Fator crítico de sucesso")}
									>
										<RCCIcon
											name="exclamationTriangle"
											size={12}
											style={{marginRight: "8px"}}
										/>
									</RCCTooltip>                                   
								}
								<WrapText lineClamp={2} title={objective.mea003Nome}>
									{objective.mea003Nome}
								</WrapText>
							</RCCGrid>
						</h5>
					</RCCGrid>
					<div className="card-body">
						<RCCGrid container>
							<RCCGrid 
								container 
								item 
								xs={12}
								styleInline="margin:5px"
							>
								<BadgeAssociate
									mapId={mapId}
									objectiveId={objective.mea003Id}
									associations={objective.associations}
									position="left"
									label={labelLayout}									
								/>
							</RCCGrid>
							<RCCGrid container item xs={12}>
								<RCCProgress 
									color={objective.color === null ? undefined : `#${objective.color}`}
									onMouseOver={() => mouseOverProgress()}
									onMouseOut={() => mouseOutProgress()}
									layout="line"
									value={objective.realizedPercentage || 0}
									styleInline="margin-right: 4px;"
								/>
							</RCCGrid>
						</RCCGrid>
					</div>
				</div>
			</ObjectiveCard>
		</>
	);
};

export default Objective;
